import { useFormContext, useWatch } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { format } from 'date-fns';
import { el } from 'date-fns/locale';
import { Calendar, ChevronRight, Copy, Download, FileClock, MoreVertical, Store, Sun, Trash2 } from 'lucide-react';
import { PolicyBadgeThumb } from './policy-badge-thumb';
import { Pill } from '~/components/pill';
import { Button } from '@/components/ui/button';
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuSeparator,
	DropdownMenuTrigger
} from '@/components/ui/dropdown-menu';
import { computePromotionStatus } from '~/lib/promotion-status';
import type { PromotionFormValues } from '~/lib/schemas/promotion';

/** Policy head — shared across all three tabs. The save button shows an amber dot when dirty. */
export function EditorHead() {
	const { t } = useTranslation();
	const {
		control,
		formState: { isDirty }
	} = useFormContext<PromotionFormValues>();
	const values = useWatch({ control }) as PromotionFormValues;

	const { activePills, visibilityPills } = computePromotionStatus({
		isActive: values.isActive,
		activeFrom: values.activeFrom,
		activeTo: values.activeTo,
		code: values.code || null,
		actionCount: values.actions?.length ?? 0,
		isVisible: values.isVisible,
		hideFromOffers: values.hideFromOffers
	});
	// Head shows active + visibility + a compact validity-range pill (the lifecycle
	// pill is reserved for the right rail, per 06-editor-spec.md "Policy head").
	const pills = [...activePills, ...visibilityPills];
	const validityRange =
		values.activeFrom && values.activeTo
			? t('promotion.rail.validFromTo', {
					from: format(values.activeFrom, 'dd MMM', { locale: el }),
					to: format(values.activeTo, 'dd MMM', { locale: el })
				})
			: null;

	return (
		<div className="rounded-lg border border-border bg-surface p-4 px-5">
			<div className="mb-3 flex items-center gap-1 text-[11px] text-foreground-subtle">
				<span>{t('promotion.crumbs.offers')}</span>
				<ChevronRight size={12} />
				<span>{t('promotion.crumbs.policies')}</span>
				<ChevronRight size={12} />
				<span className="text-foreground-muted">{values.name}</span>
			</div>

			<div className="grid grid-cols-[64px_1fr_auto] items-center gap-4">
				<PolicyBadgeThumb gradient="sun" icon={Sun} size="lg" />

				<div>
					<h1 className="text-2xl font-semibold tracking-[-0.015em] text-foreground">{values.name}</h1>
					<div className="mt-1 flex flex-wrap items-center gap-2 text-sm text-foreground-muted">
						<span>
							{t('promotion.basicInfo.identity.code')}{' '}
							<span className="rounded bg-code px-1.5 py-0.5 font-mono text-xs text-foreground">{values.code}</span>
						</span>
						<span>·</span>
						<span>
							{t('promotion.basicInfo.identity.priority')} <strong className="text-foreground">{values.priority}</strong>
						</span>
						{pills.map(p => (
							<Pill key={p.labelKey} variant={p.variant} icon={p.icon}>
								{t(p.labelKey)}
							</Pill>
						))}
						{validityRange && (
							<Pill variant="info" icon={Calendar}>
								{validityRange}
							</Pill>
						)}
					</div>
				</div>

				<div className="flex items-center gap-2">
					<DropdownMenu>
						<DropdownMenuTrigger asChild>
							<Button variant="outline" size="icon" type="button" aria-label="more">
								<MoreVertical size={16} />
							</Button>
						</DropdownMenuTrigger>
						<DropdownMenuContent align="end">
							<DropdownMenuItem>
								<Copy size={14} /> {t('promotion.grid.rowMenu.duplicate')}
							</DropdownMenuItem>
							<DropdownMenuItem>
								<Store size={14} /> {t('promotion.grid.rowMenu.viewInStore')}
							</DropdownMenuItem>
							<DropdownMenuItem>
								<Download size={14} /> {t('promotion.grid.rowMenu.exportJson')}
							</DropdownMenuItem>
							<DropdownMenuItem>
								<FileClock size={14} /> {t('promotion.grid.rowMenu.history')}
							</DropdownMenuItem>
							<DropdownMenuSeparator />
							<DropdownMenuItem variant="destructive">
								<Trash2 size={14} /> {t('common.delete')}
							</DropdownMenuItem>
						</DropdownMenuContent>
					</DropdownMenu>

					<Button type="submit" className="relative">
						{t('common.save')}
						{isDirty && <span className="absolute -right-1 -top-1 h-[7px] w-[7px] rounded-full bg-amber-500" />}
					</Button>
				</div>
			</div>
		</div>
	);
}
